import { create } from "zustand";

import { publicEnv } from "../app/env";
import { detectPerformanceTier, type ColorId } from "../design/productConfig";
import {
  loadMockDriver,
  saveMockDriver,
} from "../features/driver/mockIdentity";
import { enableAudio, playClick, playHorn } from "../features/driver/sound";
import { mockLocationFor } from "../features/location/coarseLocation";
import { PrimaryTabCoordinator } from "../features/session/PrimaryTabCoordinator";
import { modeCapabilities } from "../features/session/loadShedding";
import {
  inferSessionDistanceOrigin,
  isSessionBlocked,
  limitRemoteDrivers,
  prepareRemoteSnapshot,
  reconcileSessionDistance,
  remoteStaleThresholdMs,
  remoteIdsMissingFromLiveSet,
  stampRemotePresence,
  syncSelectedRemote,
} from "../features/session/remoteDrivers";
import { referralCodeFromLocation } from "../features/sharing/shareRoute";
import {
  addedDelayIncrement,
  enduredDelaySeconds,
  smoothSpeed,
  targetSpeedMph,
  trafficStateForSpeed,
  trafficStateWithHysteresis,
  trafficWaveMultiplier,
  worseTrafficState,
} from "../features/traffic/trafficModel";
import { analytics } from "../services/analytics/analytics";
import { fetchViewerLocation } from "../services/location/locationApi";
import { createTrafficServices } from "../services/createTrafficServices";
import { errors } from "../services/errors/errors";
import { createMockDrivers } from "../services/realtime/MockTrafficRealtimeProvider";
import {
  installRealtimeDiagnostics,
  recordRealtimeDiagnostic,
} from "../services/realtime/realtimeDiagnostics";
import { SessionExpiredError } from "../services/traffic-api/NetlifyTrafficApi";
import type {
  CoarseLocation,
  ConnectionState,
  DriverProfile,
  FreeVehiclePreset,
  Lane,
  PerformanceTier,
  RealtimeMode,
  Reaction,
  RemoteDriver,
  TrafficEvent,
  TrafficState,
  TripReport,
} from "../types/traffic";

export type ExperiencePhase =
  "spectating" | "merging" | "driving" | "exiting" | "report";

type TrafficStore = {
  phase: ExperiencePhase;
  sessionId: string | null;
  sessionJoinedAt: string | null;
  profile: DriverProfile | null;
  returningDriver: boolean;
  remoteDrivers: RemoteDriver[];
  visibleRemoteCount: number;
  connectionState: ConnectionState;
  realtimeMode: RealtimeMode;
  transportMode: "mock" | "supabase" | "offline";
  isPrimaryTab: boolean;
  cohortId: string | null;
  cohortChannel: string | null;
  serverTimeOffset: number;
  heartbeatIntervalSeconds: number;
  configurationRevision: number;
  driverCount: number;
  targetSpeed: number;
  baseSpeed: number;
  displayedSpeed: number;
  trafficState: TrafficState;
  lane: Lane;
  laneChangeSequence: number;
  boundarySequence: number;
  hornSequence: number;
  flashSequence: number;
  reactionSequence: number;
  lastReaction: Reaction | null;
  remoteEventSequence: number;
  lastRemoteEvent: TrafficEvent | null;
  selectedRemote: RemoteDriver | null;
  reactionPickerOpen: boolean;
  garageOpen: boolean;
  infoOpen: boolean;
  soundEnabled: boolean;
  reducedMotion: boolean;
  performanceTier: PerformanceTier;
  canvasFailed: boolean;
  elapsedSeconds: number;
  distanceMiles: number;
  addedDelaySeconds: number;
  peakDriverCount: number;
  worstState: TrafficState;
  report: TripReport | null;
  mergeError: string | null;
  viewerLocation: CoarseLocation | null;
  initializeSessionRuntime: () => void;
  shutdownSessionRuntime: () => void;
  setDocumentVisible: (visible: boolean) => void;
  merge: () => Promise<void>;
  mergeAgain: () => Promise<void>;
  changeLane: (
    direction: -1 | 1,
    inputType: "keyboard" | "pointer" | "touch",
  ) => void;
  honk: () => void;
  flashLights: () => void;
  toggleReactionPicker: () => void;
  react: (reaction: Reaction) => void;
  setDriverCount: (count: number) => void;
  tick: (deltaSeconds: number) => void;
  takeExit: () => void;
  dismissReport: () => void;
  inspectRemote: (driver: RemoteDriver | null) => void;
  setVisibleRemoteCount: (count: number) => void;
  setGarageOpen: (open: boolean) => void;
  replaceProfile: (profile: DriverProfile) => void;
  selectFreeVehicle: (preset: FreeVehiclePreset) => void;
  selectFreeColor: (colorId: ColorId) => void;
  setInfoOpen: (open: boolean) => void;
  toggleSound: () => Promise<void>;
  toggleReducedMotion: () => void;
  setPerformanceTier: (tier: PerformanceTier) => void;
  setCanvasFailed: () => void;
};

const configuredMockMode =
  publicEnv.VITE_REALTIME_MODE === "mock" &&
  publicEnv.VITE_APP_ENV !== "production";
const initialDriverCount = configuredMockMode ? 24 : 1;
const initialSpeed = targetSpeedMph(initialDriverCount);
const performanceTier = detectPerformanceTier();
const services = createTrafficServices(performanceTier, initialDriverCount);
const trafficApi = services.api;
const realtimeProvider = services.realtime;
const mockMode = services.transportMode === "mock";
const tabCoordinator = new PrimaryTabCoordinator();

let laneLockedUntil = 0;
let lastHornAt = 0;
let lastFlashAt = 0;
let lastReactionAt = 0;
let heartbeatTimer: number | null = null;
let hiddenReleaseTimer: number | null = null;
let presenceTimer: number | null = null;
let realtimeReconnectTimer: number | null = null;
let rosterRepairTimer: number | null = null;
const leaveGraceTimers = new Map<string, number>();
const departRemoveTimers = new Map<string, number>();
const departedUntil = new Map<string, number>();
const LEAVE_GRACE_MS = 8_000;
const DEPART_ANIMATION_MS = 1_800;
const DEPART_BLOCK_MS = 45_000;
const LANE_EVENT_HOLD_MS = 2_500;
const PRESENCE_REFRESH_MS = 12_000;
const ROSTER_REPAIR_DELAY_MS = 2_500;
const recentRemoteLanes = new Map<string, { lane: Lane; at: number }>();
let primaryDisconnectTimer: number | null = null;
let connectionOfflineTimer: number | null = null;
const CONNECTION_GRACE_MS = 4_000;
let lastHeartbeatDistance = 0;
let sessionDistanceBaseMiles = 0;
let serverSessionDistanceOriginMiles: number | null = null;
let heartbeatFailures = 0;
let reconnectFailures = 0;
let lastPresenceSnapshotCount = 0;
let browserNetworkOffline = !navigator.onLine;
let runtimeInitialized = false;
const SOUND_PREFERENCE_KEY = "internet-traffic-jam:sound-enabled";

function loadSoundPreference(): boolean {
  try {
    return localStorage.getItem(SOUND_PREFERENCE_KEY) === "true";
  } catch {
    return false;
  }
}

function saveSoundPreference(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_PREFERENCE_KEY, String(enabled));
  } catch {
    // A blocked storage write should not prevent sound in this session.
  }
}

function remoteStateFor(
  profile: DriverProfile,
  sessionId: string,
  lane: Lane,
  joinedAt: string,
  sessionElapsedSeconds: number,
  sessionDistanceMiles: number,
  location: CoarseLocation | null,
): RemoteDriver {
  return {
    sessionId,
    publicDriverId: profile.publicDriverId,
    lane,
    joinedAt,
    vehiclePreset: profile.vehiclePreset,
    colorId: profile.colorId,
    plate: profile.plate,
    badge: profile.badge,
    sessionElapsedSeconds,
    sessionDistanceMiles,
    location,
  };
}

export const useTrafficStore = create<TrafficStore>((set, get) => {
  realtimeProvider.subscribe((update) => {
    if (update.type === "snapshot") {
      lastPresenceSnapshotCount = update.drivers.length;
      recordRealtimeDiagnostic("store.presence.snapshot", {
        incomingCount: update.drivers.length,
        currentCount: useTrafficStore.getState().remoteDrivers.length,
      });
      set((state) => {
        const now = Date.now();
        const prepared = prepareRemoteSnapshot({
          current: state.remoteDrivers,
          incoming: update.drivers.map((driver) =>
            stampRemotePresence(applyHeldLane(driver), now),
          ),
          localSessionId: state.sessionId,
          localPublicDriverId: state.profile?.publicDriverId ?? null,
          departedUntil,
          now,
          performanceTier: state.performanceTier,
        });
        for (const sessionId of prepared.revivedSessionIds) {
          reviveRemoteSession(sessionId);
        }
        for (const sessionId of prepared.missingSessionIds) {
          scheduleLeaveGrace(sessionId, "presence-snapshot");
        }
        return {
          remoteDrivers: prepared.remoteDrivers,
          selectedRemote: syncSelectedRemote(
            state.selectedRemote,
            prepared.remoteDrivers,
          ),
        };
      });
      if (update.drivers.length > 0) clearRosterRepair();
    }
    if (update.type === "leave") {
      scheduleLeaveGrace(update.sessionId, "presence-leave");
    }
    if (update.type === "join") {
      lastPresenceSnapshotCount = Math.max(1, lastPresenceSnapshotCount);
      const local = useTrafficStore.getState();
      if (
        update.driver.sessionId === local.sessionId ||
        update.driver.publicDriverId === local.profile?.publicDriverId
      ) {
        return;
      }
      if (isSessionBlocked(update.driver.sessionId, departedUntil)) return;
      reviveRemoteSession(update.driver.sessionId);
      set((state) => {
        const stamped = stampRemotePresence(applyHeldLane(update.driver));
        const merged = state.remoteDrivers.some(
          (driver) =>
            driver.sessionId === update.driver.sessionId ||
            driver.publicDriverId === update.driver.publicDriverId,
        )
          ? state.remoteDrivers.map((driver) =>
              driver.sessionId === update.driver.sessionId ||
              driver.publicDriverId === update.driver.publicDriverId
                ? { ...stamped, departing: false }
                : driver,
            )
          : [...state.remoteDrivers, stamped];
        const remoteDrivers = limitRemoteDrivers(merged, state.performanceTier);
        return {
          remoteDrivers,
          selectedRemote: syncSelectedRemote(
            state.selectedRemote,
            remoteDrivers,
          ),
        };
      });
    }
    if (update.type === "event") {
      if (update.event.type === "exit") {
        beginRemoteDeparture(update.event.sessionId, true);
      }
      if (update.event.type === "lane") {
        const { sessionId, lane } = update.event;
        recentRemoteLanes.set(sessionId, { lane, at: Date.now() });
        reviveRemoteSession(sessionId);
        set((state) => {
          const remoteDrivers = state.remoteDrivers.map((driver) =>
            driver.sessionId === sessionId
              ? { ...driver, lane, departing: false }
              : driver,
          );
          return {
            remoteDrivers,
            selectedRemote: syncSelectedRemote(
              state.selectedRemote,
              remoteDrivers,
            ),
          };
        });
        return;
      }
      set((state) => ({
        remoteEventSequence: state.remoteEventSequence + 1,
        lastRemoteEvent: update.event,
      }));
    }
    if (update.type === "connection") {
      recordRealtimeDiagnostic("store.connection", {
        state: update.state,
        channel: useTrafficStore.getState().cohortChannel,
      });
      if (browserNetworkOffline) {
        set({ connectionState: "offline" });
        return;
      }
      if (update.state === "connected") {
        reconnectFailures = 0;
        clearConnectionGrace();
        set({ connectionState: "connected" });
        clearRealtimeReconnect();
        return;
      }
      if (update.state === "connecting") {
        set((state) =>
          state.connectionState === "connected"
            ? state
            : { connectionState: "connecting" },
        );
        return;
      }
      scheduleRealtimeReconnect();
      if (connectionOfflineTimer !== null) return;
      connectionOfflineTimer = window.setTimeout(() => {
        connectionOfflineTimer = null;
        useTrafficStore.setState({ connectionState: "offline" });
      }, CONNECTION_GRACE_MS);
    }
  });

  return {
    phase: "spectating",
    sessionId: null,
    sessionJoinedAt: null,
    profile: loadMockDriver(),
    returningDriver: loadMockDriver() !== null,
    remoteDrivers: mockMode
      ? createMockDrivers(initialDriverCount, performanceTier)
      : [],
    visibleRemoteCount: 0,
    connectionState: mockMode ? "connected" : "offline",
    realtimeMode: mockMode
      ? "mock"
      : services.transportMode === "supabase"
        ? "full"
        : "offline",
    transportMode: services.transportMode,
    isPrimaryTab: true,
    cohortId: null,
    cohortChannel: null,
    serverTimeOffset: 0,
    heartbeatIntervalSeconds: 30,
    configurationRevision: 1,
    driverCount: initialDriverCount,
    targetSpeed: initialSpeed,
    baseSpeed: initialSpeed,
    displayedSpeed: initialSpeed,
    trafficState: trafficStateForSpeed(initialSpeed),
    lane: 4,
    laneChangeSequence: 0,
    boundarySequence: 0,
    hornSequence: 0,
    flashSequence: 0,
    reactionSequence: 0,
    lastReaction: null,
    remoteEventSequence: 0,
    lastRemoteEvent: null,
    selectedRemote: null,
    reactionPickerOpen: false,
    garageOpen: false,
    infoOpen: false,
    soundEnabled: loadSoundPreference(),
    reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches,
    performanceTier,
    canvasFailed: false,
    elapsedSeconds: 0,
    distanceMiles: 0,
    addedDelaySeconds: 0,
    peakDriverCount: initialDriverCount,
    worstState: trafficStateForSpeed(initialSpeed),
    report: null,
    mergeError: null,
    viewerLocation: mockMode
      ? mockLocationFor(loadMockDriver()?.plate ?? "viewer")
      : null,

    initializeSessionRuntime: () => {
      if (runtimeInitialized) return;
      runtimeInitialized = true;
      installRealtimeDiagnostics();
      if (!mockMode && services.transportMode === "supabase") {
        void fetchViewerLocation().then((location) => {
          if (location) useTrafficStore.setState({ viewerLocation: location });
        });
      }
      window.addEventListener("offline", handleBrowserOffline);
      window.addEventListener("online", handleBrowserOnline);
      tabCoordinator.start();
      tabCoordinator.subscribe((primary) => {
        set({ isPrimaryTab: primary });
        if (!primary) {
          clearHeartbeatTimer();
          clearPresenceTimer();
          clearRealtimeReconnect();
          clearRosterRepair();
          clearConnectionGrace();
          if (primaryDisconnectTimer !== null) {
            window.clearTimeout(primaryDisconnectTimer);
          }
          primaryDisconnectTimer = window.setTimeout(() => {
            primaryDisconnectTimer = null;
            if (!useTrafficStore.getState().isPrimaryTab) {
              void realtimeProvider.disconnect();
            }
          }, 400);
        } else if (get().phase === "driving" && get().sessionId) {
          if (primaryDisconnectTimer !== null) {
            window.clearTimeout(primaryDisconnectTimer);
            primaryDisconnectTimer = null;
          }
          void resumeRealtimePresence();
          startPresenceTimer();
          scheduleHeartbeat(250);
        }
      });
    },

    shutdownSessionRuntime: () => {
      runtimeInitialized = false;
      clearHeartbeatTimer();
      clearPresenceTimer();
      clearRealtimeReconnect();
      clearRosterRepair();
      clearLeaveGrace();
      clearConnectionGrace();
      if (primaryDisconnectTimer !== null) {
        window.clearTimeout(primaryDisconnectTimer);
        primaryDisconnectTimer = null;
      }
      if (hiddenReleaseTimer !== null) {
        window.clearTimeout(hiddenReleaseTimer);
        hiddenReleaseTimer = null;
      }
      window.removeEventListener("offline", handleBrowserOffline);
      window.removeEventListener("online", handleBrowserOnline);
      tabCoordinator.stop();
    },

    setDocumentVisible: (visible) => {
      if (hiddenReleaseTimer !== null) {
        window.clearTimeout(hiddenReleaseTimer);
        hiddenReleaseTimer = null;
      }
      if (!visible) {
        hiddenReleaseTimer = window.setTimeout(() => {
          void (async () => {
            clearHeartbeatTimer();
            clearPresenceTimer();
            clearRealtimeReconnect();
            clearRosterRepair();
            await heartbeatNow(false, false);
            await realtimeProvider.disconnect();
            set({ connectionState: "offline", remoteDrivers: [] });
          })();
        }, 20_000);
        return;
      }
      if (get().phase === "driving" && get().isPrimaryTab) {
        void resumeRealtimePresence();
        startPresenceTimer();
        scheduleHeartbeat(250);
      }
    },

    merge: async () => {
      if (!["spectating", "report"].includes(get().phase)) return;
      lastPresenceSnapshotCount = 0;
      if (!get().isPrimaryTab && !tabCoordinator.claimPrimary()) {
        set({ mergeError: "Merge from a visible primary tab." });
        return;
      }
      if (mockMode) {
        get().setDriverCount(Math.min(100_000, get().driverCount + 1));
      }
      const beforeMerge = get();
      const referralCode =
        referralCodeFromLocation(location.pathname, location.search) ??
        undefined;
      analytics.track("merge_started", {
        returning: beforeMerge.returningDriver,
        referralPresent: Boolean(referralCode),
      });
      set({
        phase: "merging",
        report: null,
        mergeError: null,
        elapsedSeconds: 0,
        distanceMiles: 0,
        addedDelaySeconds: 0,
        peakDriverCount: get().driverCount,
        worstState: get().trafficState,
      });
      sessionDistanceBaseMiles = 0;
      serverSessionDistanceOriginMiles = null;

      try {
        const response = await trafficApi.join({
          ...(referralCode ? { referralCode } : {}),
          ...(beforeMerge.profile && !beforeMerge.profile.garageOwned
            ? {
                preferredAppearance: {
                  vehiclePreset: beforeMerge.profile.vehiclePreset,
                  colorId: beforeMerge.profile.colorId,
                },
              }
            : {}),
        });
        const lane: Lane = 4;
        const realtimeMode = response.features.realtimeMode;
        const sessionJoinedAt = new Date().toISOString();
        saveMockDriver(response.driver);
        set({
          sessionId: response.sessionId,
          sessionJoinedAt,
          profile: response.driver,
          returningDriver: true,
          lane,
          driverCount: response.traffic.activeDriverCount,
          targetSpeed: response.traffic.targetSpeedMph,
          realtimeMode,
          cohortId: response.cohort.id,
          cohortChannel: response.cohort.channel,
          serverTimeOffset:
            new Date(response.serverTime).getTime() - Date.now(),
          heartbeatIntervalSeconds: response.heartbeatIntervalSeconds,
          configurationRevision: response.configurationRevision,
          viewerLocation: response.location ?? get().viewerLocation,
          connectionState:
            modeCapabilities(realtimeMode).presence && get().isPrimaryTab
              ? "connecting"
              : "offline",
        });
        lastHeartbeatDistance = 0;
        if (get().isPrimaryTab && modeCapabilities(realtimeMode).presence) {
          await realtimeProvider.connect({
            sessionId: response.sessionId,
            channel: response.cohort.channel,
            driver: remoteStateFor(
              response.driver,
              response.sessionId,
              lane,
              sessionJoinedAt,
              0,
              0,
              response.location ?? get().viewerLocation,
            ),
          });
          startPresenceTimer();
        }
        scheduleHeartbeat(response.heartbeatIntervalSeconds * 1_000);
        window.setTimeout(
          () => {
            if (get().phase !== "merging") return;
            set({ phase: "driving" });
            analytics.track("merge_completed", {
              trafficBucket: response.traffic.state,
              cohortSize: get().remoteDrivers.length,
              referralPresent: Boolean(referralCode),
            });
          },
          get().reducedMotion ? 350 : 1550,
        );
      } catch (error) {
        errors.capture(error, { action: "session_merge" });
        set({
          phase: "spectating",
          connectionState: "offline",
          sessionId: null,
          sessionJoinedAt: null,
          mergeError: "Could not merge. The radio is still offline.",
        });
      }
    },

    mergeAgain: async () => {
      await get().merge();
    },

    changeLane: (direction, inputType) => {
      const state = get();
      if (state.phase !== "driving" || performance.now() < laneLockedUntil)
        return;
      const candidate = state.lane + direction;
      if (candidate < 1 || candidate > 6) {
        set((current) => ({
          boundarySequence: current.boundarySequence + 1,
        }));
        if (state.soundEnabled) playClick();
        return;
      }

      const lane = candidate as Lane;
      laneLockedUntil = performance.now() + (state.reducedMotion ? 120 : 520);
      set((current) => ({
        lane,
        laneChangeSequence: current.laneChangeSequence + 1,
        selectedRemote: null,
      }));
      if (
        state.sessionId &&
        state.isPrimaryTab &&
        modeCapabilities(state.realtimeMode).presence
      ) {
        void realtimeProvider.publish({
          type: "lane",
          sessionId: state.sessionId,
          lane,
          at: Date.now(),
        });
        void realtimeProvider.updateLane(lane);
      }
      analytics.track("lane_changed", {
        inputType,
        from: state.lane,
        to: lane,
      });
    },

    honk: () => {
      const state = get();
      const now = Date.now();
      if (
        state.phase !== "driving" ||
        now - lastHornAt <
          1000 + modeCapabilities(state.realtimeMode).eventThrottleMs
      ) {
        return;
      }
      lastHornAt = now;
      set((current) => ({ hornSequence: current.hornSequence + 1 }));
      if (state.soundEnabled) playHorn(state.profile?.hornId ?? "classic");
      if (
        state.sessionId &&
        state.isPrimaryTab &&
        modeCapabilities(state.realtimeMode).interactions
      ) {
        void realtimeProvider.publish({
          type: "honk",
          sessionId: state.sessionId,
          at: now,
        });
      }
      analytics.track("horn_used", {
        hornId: state.profile?.hornId ?? "classic",
        trafficState: state.trafficState,
      });
    },

    flashLights: () => {
      const state = get();
      const now = Date.now();
      if (
        state.phase !== "driving" ||
        now - lastFlashAt <
          1500 + modeCapabilities(state.realtimeMode).eventThrottleMs
      ) {
        return;
      }
      lastFlashAt = now;
      set((current) => ({ flashSequence: current.flashSequence + 1 }));
      if (
        state.sessionId &&
        state.isPrimaryTab &&
        modeCapabilities(state.realtimeMode).interactions
      ) {
        void realtimeProvider.publish({
          type: "flash",
          sessionId: state.sessionId,
          at: now,
        });
      }
    },

    toggleReactionPicker: () => {
      if (get().phase === "driving") {
        set((state) => ({ reactionPickerOpen: !state.reactionPickerOpen }));
      }
    },

    react: (reaction) => {
      const state = get();
      const now = Date.now();
      if (
        state.phase !== "driving" ||
        now - lastReactionAt <
          2000 + modeCapabilities(state.realtimeMode).eventThrottleMs
      ) {
        return;
      }
      lastReactionAt = now;
      set((current) => ({
        reactionSequence: current.reactionSequence + 1,
        lastReaction: reaction,
        reactionPickerOpen: false,
      }));
      if (
        state.sessionId &&
        state.isPrimaryTab &&
        modeCapabilities(state.realtimeMode).interactions
      ) {
        void realtimeProvider.publish({
          type: "reaction",
          sessionId: state.sessionId,
          reaction,
          at: now,
        });
      }
      analytics.track("reaction_used", { reaction });
    },

    setDriverCount: (count) => {
      if (!mockMode) return;
      const driverCount = Math.min(100_000, Math.max(1, Math.floor(count)));
      const targetSpeed = targetSpeedMph(driverCount);
      services.setSimulatedDriverCount(driverCount);
      set((state) => ({
        driverCount,
        targetSpeed,
        peakDriverCount: Math.max(state.peakDriverCount, driverCount),
        worstState: worseTrafficState(
          state.worstState,
          trafficStateForSpeed(targetSpeed),
        ),
      }));
    },

    tick: (deltaSeconds) => {
      const state = get();
      if (
        state.phase !== "driving" ||
        document.hidden ||
        deltaSeconds <= 0 ||
        deltaSeconds > 2
      ) {
        return;
      }
      const baseSpeed = smoothSpeed(
        state.baseSpeed,
        state.targetSpeed,
        deltaSeconds,
      );
      const wave = trafficWaveMultiplier(Date.now(), state.targetSpeed);
      const displayedSpeed = Math.max(0.5, Math.min(68, baseSpeed * wave));
      const trafficState = trafficStateWithHysteresis(
        state.trafficState,
        displayedSpeed,
      );
      const distanceDelta = (displayedSpeed * deltaSeconds) / 3600;
      const now = Date.now();
      const remoteStaleMs = remoteStaleThresholdMs(
        state.heartbeatIntervalSeconds,
      );
      for (const driver of state.remoteDrivers) {
        if (driver.departing) continue;
        if (
          state.transportMode !== "mock" &&
          now - (driver.presenceReceivedAt ?? now) > remoteStaleMs
        ) {
          scheduleLeaveGrace(driver.sessionId, "presence-stale");
        }
      }
      set({
        baseSpeed,
        displayedSpeed,
        trafficState,
        elapsedSeconds: state.elapsedSeconds + deltaSeconds,
        distanceMiles: state.distanceMiles + distanceDelta,
        addedDelaySeconds:
          state.addedDelaySeconds +
          addedDelayIncrement(deltaSeconds, state.targetSpeed),
      });
    },

    takeExit: () => {
      const state = get();
      if (state.phase !== "driving" || !state.profile) return;
      lastPresenceSnapshotCount = 0;
      analytics.track("exit_started", {
        sessionDurationBucket: Math.floor(state.elapsedSeconds / 15) * 15,
      });
      set({
        phase: "exiting",
        reactionPickerOpen: false,
        selectedRemote: null,
        garageOpen: false,
        infoOpen: false,
      });
      clearHeartbeatTimer();
      clearPresenceTimer();
      clearRealtimeReconnect();
      clearRosterRepair();
      clearLeaveGrace();
      clearConnectionGrace();
      const sessionId = state.sessionId;
      const announceExit =
        Boolean(sessionId) &&
        state.isPrimaryTab &&
        modeCapabilities(state.realtimeMode).presence;
      void (async () => {
        if (announceExit && sessionId) {
          try {
            await realtimeProvider.publish({
              type: "exit",
              sessionId,
              at: Date.now(),
            });
          } catch {
            // Presence untrack still runs even if the exit broadcast misses.
          }
        }
        await realtimeProvider.disconnect();
        if (sessionId && state.isPrimaryTab && !mockMode) {
          void trafficApi.leave(sessionId);
        }
      })();
      if (mockMode) {
        get().setDriverCount(Math.max(1, state.driverCount - 1));
      }

      window.setTimeout(
        () => {
          const latest = get();
          if (!latest.profile || latest.phase !== "exiting") return;
          const profile = {
            ...latest.profile,
            odometerMiles: latest.profile.odometerMiles + latest.distanceMiles,
          };
          saveMockDriver(profile);
          const report: TripReport = {
            profile,
            elapsedSeconds: latest.elapsedSeconds,
            distanceMiles: latest.distanceMiles,
            peakDriverCount: latest.peakDriverCount,
            worstState: latest.worstState,
            enduredDelaySeconds: enduredDelaySeconds(
              latest.distanceMiles,
              latest.elapsedSeconds,
            ),
            addedDelaySeconds: latest.addedDelaySeconds,
            finishedAt: new Date().toISOString(),
            location: latest.viewerLocation,
          };
          set({
            phase: "report",
            profile,
            report,
            sessionId: null,
            sessionJoinedAt: null,
            cohortId: null,
            cohortChannel: null,
            remoteDrivers: [],
          });
          analytics.track("trip_report_viewed", {
            trafficState: report.worstState,
            peakCountBucket: Math.floor(report.peakDriverCount / 100) * 100,
          });
        },
        state.reducedMotion ? 450 : 1900,
      );
    },

    dismissReport: () => {
      if (get().phase !== "report") return;
      set({
        phase: "spectating",
        report: null,
        sessionId: null,
        sessionJoinedAt: null,
        cohortId: null,
        cohortChannel: null,
      });
    },

    inspectRemote: (driver) => set({ selectedRemote: driver }),
    setVisibleRemoteCount: (count) =>
      set({ visibleRemoteCount: Math.max(0, Math.floor(count)) }),
    setGarageOpen: (open) => {
      set({ garageOpen: open });
      if (open) {
        analytics.track("garage_opened", {
          owned: get().profile?.garageOwned ?? false,
        });
      }
    },
    replaceProfile: (profile) => {
      if (mockMode) saveMockDriver(profile);
      set({ profile });
      void publishCurrentPresence();
    },
    selectFreeVehicle: (preset) => {
      const profile = get().profile;
      if (!profile) return;
      const nextProfile = { ...profile, vehiclePreset: preset };
      saveMockDriver(nextProfile);
      set({ profile: nextProfile });
      void publishCurrentPresence();
    },
    selectFreeColor: (colorId) => {
      const profile = get().profile;
      if (!profile) return;
      const nextProfile = { ...profile, colorId };
      saveMockDriver(nextProfile);
      set({ profile: nextProfile });
      void publishCurrentPresence();
    },
    setInfoOpen: (open) => set({ infoOpen: open }),

    toggleSound: async () => {
      const next = !get().soundEnabled;
      if (next) {
        try {
          await enableAudio();
          playClick();
        } catch (error) {
          errors.capture(error, { action: "enable_audio" });
          return;
        }
      }
      saveSoundPreference(next);
      set({ soundEnabled: next });
    },

    toggleReducedMotion: () => {
      set((state) => ({ reducedMotion: !state.reducedMotion }));
    },

    setPerformanceTier: (tier) => {
      const previous = get().performanceTier;
      if (previous === tier) return;
      services.setPerformanceTier(tier);
      set({ performanceTier: tier });
      analytics.track("performance_tier_changed", {
        from: previous,
        to: tier,
      });
    },

    setCanvasFailed: () => set({ canvasFailed: true }),
  };
});

function clearHeartbeatTimer(): void {
  if (heartbeatTimer !== null) window.clearTimeout(heartbeatTimer);
  heartbeatTimer = null;
}

function cancelLeaveGrace(sessionId: string): void {
  const timer = leaveGraceTimers.get(sessionId);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    leaveGraceTimers.delete(sessionId);
  }
}

function cancelDepartRemove(sessionId: string): void {
  const timer = departRemoveTimers.get(sessionId);
  if (timer !== undefined) {
    window.clearTimeout(timer);
    departRemoveTimers.delete(sessionId);
  }
}

function applyHeldLane(driver: RemoteDriver): RemoteDriver {
  const held = recentRemoteLanes.get(driver.sessionId);
  if (!held || Date.now() - held.at > LANE_EVENT_HOLD_MS) return driver;
  return { ...driver, lane: held.lane };
}

function reviveRemoteSession(sessionId: string): void {
  cancelLeaveGrace(sessionId);
  cancelDepartRemove(sessionId);
  departedUntil.delete(sessionId);
}

function reconcileRemoteDriversWithLiveSessions(
  liveSessionIds: readonly string[] | undefined,
): void {
  if (!liveSessionIds) return;
  recordRealtimeDiagnostic("heartbeat.cohort-roster", {
    count: liveSessionIds.length,
    sessionIds: liveSessionIds,
  });
  const localSessionId = useTrafficStore.getState().sessionId;
  const live = localSessionId
    ? [...new Set([...liveSessionIds, localSessionId])]
    : liveSessionIds;
  const liveSet = new Set(live);
  const state = useTrafficStore.getState();
  const expectedRemoteCount = live.filter(
    (sessionId) => sessionId !== localSessionId,
  ).length;
  if (
    expectedRemoteCount > 0 &&
    lastPresenceSnapshotCount === 0 &&
    state.connectionState === "connected"
  ) {
    scheduleRosterRepair(expectedRemoteCount);
  } else {
    clearRosterRepair();
  }
  for (const driver of state.remoteDrivers) {
    if (liveSet.has(driver.sessionId)) reviveRemoteSession(driver.sessionId);
  }
  for (const sessionId of remoteIdsMissingFromLiveSet(
    state.remoteDrivers,
    live,
  )) {
    scheduleLeaveGrace(sessionId, "heartbeat-roster");
  }
}

function scheduleLeaveGrace(
  sessionId: string,
  reason:
    | "presence-snapshot"
    | "presence-leave"
    | "presence-stale"
    | "heartbeat-roster",
): void {
  if (
    leaveGraceTimers.has(sessionId) ||
    departRemoveTimers.has(sessionId) ||
    isSessionBlocked(sessionId, departedUntil)
  ) {
    return;
  }
  recordRealtimeDiagnostic("remote.departure.scheduled", {
    sessionId,
    reason,
    delayMs: LEAVE_GRACE_MS,
  });
  leaveGraceTimers.set(
    sessionId,
    window.setTimeout(() => {
      leaveGraceTimers.delete(sessionId);
      recordRealtimeDiagnostic("remote.departure.confirmed", {
        sessionId,
        reason,
      });
      beginRemoteDeparture(sessionId);
    }, LEAVE_GRACE_MS),
  );
}

function beginRemoteDeparture(sessionId: string, permanent = false): void {
  cancelLeaveGrace(sessionId);
  if (permanent) {
    departedUntil.set(sessionId, Date.now() + DEPART_BLOCK_MS);
  }
  useTrafficStore.setState((state) => {
    const remoteDrivers = state.remoteDrivers.map((driver) =>
      driver.sessionId === sessionId ? { ...driver, departing: true } : driver,
    );
    return {
      remoteDrivers,
      selectedRemote: syncSelectedRemote(state.selectedRemote, remoteDrivers),
    };
  });
  if (departRemoveTimers.has(sessionId)) return;
  departRemoveTimers.set(
    sessionId,
    window.setTimeout(() => {
      departRemoveTimers.delete(sessionId);
      useTrafficStore.setState((state) => {
        const remoteDrivers = state.remoteDrivers.filter(
          (driver) => driver.sessionId !== sessionId,
        );
        return {
          remoteDrivers,
          selectedRemote: syncSelectedRemote(
            state.selectedRemote,
            remoteDrivers,
          ),
        };
      });
    }, DEPART_ANIMATION_MS),
  );
}

function clearLeaveGrace(): void {
  for (const timer of leaveGraceTimers.values()) {
    window.clearTimeout(timer);
  }
  leaveGraceTimers.clear();
  for (const timer of departRemoveTimers.values()) {
    window.clearTimeout(timer);
  }
  departRemoveTimers.clear();
  departedUntil.clear();
}

function clearConnectionGrace(): void {
  if (connectionOfflineTimer !== null) {
    window.clearTimeout(connectionOfflineTimer);
    connectionOfflineTimer = null;
  }
}

function handleBrowserOffline(): void {
  if (useTrafficStore.getState().transportMode !== "supabase") return;
  browserNetworkOffline = true;
  clearRealtimeReconnect();
  clearRosterRepair();
  recordRealtimeDiagnostic("browser.network.offline");
  useTrafficStore.setState({ connectionState: "offline" });
}

function handleBrowserOnline(): void {
  browserNetworkOffline = false;
  const state = useTrafficStore.getState();
  recordRealtimeDiagnostic("browser.network.online");
  if (
    !state.isPrimaryTab ||
    !state.sessionId ||
    !["merging", "driving"].includes(state.phase) ||
    !modeCapabilities(state.realtimeMode).presence
  ) {
    return;
  }
  void forceRealtimeReconnect("browser-online");
  scheduleHeartbeat(250);
}

function clearPresenceTimer(): void {
  if (presenceTimer !== null) window.clearInterval(presenceTimer);
  presenceTimer = null;
}

function clearRosterRepair(): void {
  if (rosterRepairTimer !== null) window.clearTimeout(rosterRepairTimer);
  rosterRepairTimer = null;
}

function scheduleRosterRepair(expectedRemoteCount: number): void {
  if (rosterRepairTimer !== null) return;
  recordRealtimeDiagnostic("radio.roster-repair.scheduled", {
    expectedRemoteCount,
    delayMs: ROSTER_REPAIR_DELAY_MS,
  });
  rosterRepairTimer = window.setTimeout(() => {
    rosterRepairTimer = null;
    const state = useTrafficStore.getState();
    if (
      state.remoteDrivers.some((driver) => !driver.departing) ||
      state.connectionState !== "connected"
    ) {
      return;
    }
    void forceRealtimeReconnect("empty-heartbeat-roster");
  }, ROSTER_REPAIR_DELAY_MS);
}

async function forceRealtimeReconnect(reason: string): Promise<void> {
  const state = useTrafficStore.getState();
  if (
    !state.sessionId ||
    !state.sessionJoinedAt ||
    !state.profile ||
    !state.cohortChannel ||
    !state.isPrimaryTab ||
    !modeCapabilities(state.realtimeMode).presence
  ) {
    return;
  }
  recordRealtimeDiagnostic("radio.forced-reconnect.started", {
    channel: state.cohortChannel,
    sessionId: state.sessionId,
    reason,
  });
  useTrafficStore.setState({ connectionState: "connecting" });
  try {
    await realtimeProvider.reconnect({
      sessionId: state.sessionId,
      channel: state.cohortChannel,
      driver: remoteStateFor(
        state.profile,
        state.sessionId,
        state.lane,
        state.sessionJoinedAt,
        state.elapsedSeconds,
        state.distanceMiles,
        state.viewerLocation,
      ),
    });
  } catch (error) {
    reconnectFailures += 1;
    errors.capture(error, { action: "realtime_forced_reconnect", reason });
    recordRealtimeDiagnostic("radio.forced-reconnect.failed", {
      channel: state.cohortChannel,
      message: error instanceof Error ? error.message : "unknown",
      reason,
    });
    useTrafficStore.setState({ connectionState: "offline" });
    scheduleRealtimeReconnect();
  }
}

function startPresenceTimer(): void {
  clearPresenceTimer();
  const state = useTrafficStore.getState();
  if (
    !state.isPrimaryTab ||
    !modeCapabilities(state.realtimeMode).presence ||
    !["merging", "driving"].includes(state.phase)
  ) {
    return;
  }
  presenceTimer = window.setInterval(() => {
    const latest = useTrafficStore.getState();
    if (!latest.profile) return;
    void publishCurrentPresence();
  }, PRESENCE_REFRESH_MS);
}

function clearRealtimeReconnect(): void {
  if (realtimeReconnectTimer !== null) {
    window.clearTimeout(realtimeReconnectTimer);
  }
  realtimeReconnectTimer = null;
}

function scheduleRealtimeReconnect(): void {
  const state = useTrafficStore.getState();
  if (
    realtimeReconnectTimer !== null ||
    browserNetworkOffline ||
    document.hidden ||
    !state.isPrimaryTab ||
    !state.sessionId ||
    !["merging", "driving"].includes(state.phase) ||
    !modeCapabilities(state.realtimeMode).presence
  ) {
    return;
  }
  const delay = Math.min(
    30_000,
    Math.round(
      1_000 * 2 ** Math.min(5, reconnectFailures) * (0.7 + Math.random() * 0.6),
    ),
  );
  realtimeReconnectTimer = window.setTimeout(() => {
    realtimeReconnectTimer = null;
    void resumeRealtimePresence();
  }, delay);
}

function scheduleHeartbeat(delayMilliseconds: number): void {
  clearHeartbeatTimer();
  const state = useTrafficStore.getState();
  const capabilities = modeCapabilities(state.realtimeMode);
  if (
    !state.sessionId ||
    !state.isPrimaryTab ||
    capabilities.heartbeatMultiplier === 0
  ) {
    return;
  }
  heartbeatTimer = window.setTimeout(
    () => {
      void heartbeatNow(true, true);
    },
    Math.max(250, delayMilliseconds * capabilities.heartbeatMultiplier),
  );
}

async function heartbeatNow(
  visible: boolean,
  reschedule: boolean,
): Promise<void> {
  const state = useTrafficStore.getState();
  if (
    !state.sessionId ||
    !state.profile ||
    !state.isPrimaryTab ||
    !["merging", "driving"].includes(state.phase)
  ) {
    return;
  }
  const odometerDeltaMiles = Math.max(
    0,
    state.distanceMiles - lastHeartbeatDistance,
  );
  try {
    const response = await trafficApi.heartbeat({
      sessionId: state.sessionId,
      lane: state.lane,
      odometerDeltaMiles,
      visible,
      appearance: {
        vehiclePreset: state.profile.vehiclePreset,
        colorId: state.profile.colorId,
      },
    });
    heartbeatFailures = 0;
    const previousMode = useTrafficStore.getState().realtimeMode;
    const currentDistance = useTrafficStore.getState().distanceMiles;
    serverSessionDistanceOriginMiles ??= inferSessionDistanceOrigin(
      response.sessionDistanceMiles,
      odometerDeltaMiles,
    );
    const currentServerTripDistance = Math.max(
      0,
      response.sessionDistanceMiles - serverSessionDistanceOriginMiles,
    );
    const reconciledDistance = reconcileSessionDistance(
      currentServerTripDistance,
      state.distanceMiles,
      currentDistance,
      sessionDistanceBaseMiles,
    );
    lastHeartbeatDistance =
      sessionDistanceBaseMiles + currentServerTripDistance;
    useTrafficStore.setState((current) => ({
      driverCount: response.traffic.activeDriverCount,
      targetSpeed: response.traffic.targetSpeedMph,
      distanceMiles: reconciledDistance,
      peakDriverCount: Math.max(
        current.peakDriverCount,
        response.traffic.activeDriverCount,
      ),
      worstState: worseTrafficState(current.worstState, response.traffic.state),
      realtimeMode: response.realtimeMode,
      heartbeatIntervalSeconds: response.heartbeatIntervalSeconds,
      configurationRevision: response.configurationRevision,
    }));
    reconcileRemoteDriversWithLiveSessions(response.cohortSessionIds);

    if (previousMode !== response.realtimeMode) {
      analytics.track("realtime_degraded", {
        from: previousMode,
        to: response.realtimeMode,
        reasonCategory: "server_config",
      });
    }
    if (visible && modeCapabilities(response.realtimeMode).presence) {
      await publishCurrentPresence();
      startPresenceTimer();
    } else if (!modeCapabilities(response.realtimeMode).presence) {
      clearPresenceTimer();
      clearRealtimeReconnect();
      await realtimeProvider.disconnect();
      useTrafficStore.setState({
        connectionState: "offline",
        remoteDrivers: [],
      });
    }
    if (reschedule) {
      scheduleHeartbeat(response.heartbeatIntervalSeconds * 1_000);
    }
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      await resumeSession();
      return;
    }
    heartbeatFailures += 1;
    const current = useTrafficStore.getState();
    errors.capture(error, { action: "session_heartbeat" });
    recordRealtimeDiagnostic("heartbeat.failed", {
      failures: heartbeatFailures,
      message: error instanceof Error ? error.message : "unknown",
    });
    // HTTP heartbeat failures and Realtime socket failures are independent.
    // Keep valid presence and local controls alive while the API retries.
    if (!realtimeProvider.isConnected()) {
      scheduleRealtimeReconnect();
    }
    if (
      reschedule &&
      modeCapabilities(current.realtimeMode).heartbeatMultiplier > 0
    ) {
      scheduleHeartbeat(Math.min(30_000, 1_000 * 2 ** heartbeatFailures));
    }
  }
}

async function resumeSession(): Promise<void> {
  const state = useTrafficStore.getState();
  if (!state.profile || !state.isPrimaryTab) return;
  lastPresenceSnapshotCount = 0;
  clearLeaveGrace();
  recentRemoteLanes.clear();
  clearRealtimeReconnect();
  useTrafficStore.setState({
    connectionState: "connecting",
    remoteDrivers: [],
    visibleRemoteCount: 0,
    selectedRemote: null,
    cohortId: null,
    cohortChannel: null,
  });
  sessionDistanceBaseMiles = state.distanceMiles;
  lastHeartbeatDistance = state.distanceMiles;
  serverSessionDistanceOriginMiles = null;
  try {
    await realtimeProvider.disconnect();
    const response = await trafficApi.join(
      state.profile.garageOwned
        ? {}
        : {
            preferredAppearance: {
              vehiclePreset: state.profile.vehiclePreset,
              colorId: state.profile.colorId,
            },
          },
    );
    const joinedAt = new Date().toISOString();
    const latestProfile = useTrafficStore.getState().profile ?? state.profile;
    const profile = {
      ...response.driver,
      vehiclePreset: latestProfile.vehiclePreset,
      colorId: latestProfile.colorId,
    };
    saveMockDriver(profile);
    useTrafficStore.setState({
      sessionId: response.sessionId,
      sessionJoinedAt: joinedAt,
      profile,
      driverCount: response.traffic.activeDriverCount,
      targetSpeed: response.traffic.targetSpeedMph,
      cohortId: response.cohort.id,
      cohortChannel: response.cohort.channel,
      serverTimeOffset: new Date(response.serverTime).getTime() - Date.now(),
      heartbeatIntervalSeconds: response.heartbeatIntervalSeconds,
      configurationRevision: response.configurationRevision,
      realtimeMode: response.features.realtimeMode,
    });
    if (modeCapabilities(response.features.realtimeMode).presence) {
      await realtimeProvider.connect({
        sessionId: response.sessionId,
        channel: response.cohort.channel,
        driver: remoteStateFor(
          profile,
          response.sessionId,
          state.lane,
          joinedAt,
          state.elapsedSeconds,
          state.distanceMiles,
          useTrafficStore.getState().viewerLocation,
        ),
      });
      startPresenceTimer();
    }
    scheduleHeartbeat(response.heartbeatIntervalSeconds * 1_000);
  } catch (error) {
    errors.capture(error, { action: "session_resume" });
    useTrafficStore.setState({ connectionState: "offline" });
  }
}

async function resumeRealtimePresence(): Promise<void> {
  const state = useTrafficStore.getState();
  if (
    !state.sessionId ||
    !state.sessionJoinedAt ||
    !state.profile ||
    !state.cohortChannel ||
    !state.isPrimaryTab ||
    !modeCapabilities(state.realtimeMode).presence
  ) {
    return;
  }
  try {
    await realtimeProvider.connect({
      sessionId: state.sessionId,
      channel: state.cohortChannel,
      driver: remoteStateFor(
        state.profile,
        state.sessionId,
        state.lane,
        state.sessionJoinedAt,
        state.elapsedSeconds,
        state.distanceMiles,
        state.viewerLocation,
      ),
    });
    startPresenceTimer();
  } catch (error) {
    reconnectFailures += 1;
    errors.capture(error, { action: "realtime_resume" });
    recordRealtimeDiagnostic("radio.reconnect.failed", {
      failures: reconnectFailures,
      message: error instanceof Error ? error.message : "unknown",
    });
    useTrafficStore.setState({ connectionState: "offline" });
    scheduleRealtimeReconnect();
  }
}

async function publishCurrentPresence(): Promise<void> {
  const state = useTrafficStore.getState();
  if (
    !state.sessionId ||
    !state.sessionJoinedAt ||
    !state.profile ||
    !state.isPrimaryTab ||
    !modeCapabilities(state.realtimeMode).presence
  ) {
    return;
  }
  await realtimeProvider.updatePresence(
    remoteStateFor(
      state.profile,
      state.sessionId,
      state.lane,
      state.sessionJoinedAt,
      state.elapsedSeconds,
      state.distanceMiles,
      state.viewerLocation,
    ),
  );
}
